import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Users, AlertCircle, ChevronRight, GraduationCap, Phone, Mail, Globe } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/services/api";

interface Institution {
    id: number;
    name: string;
}

interface Student {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    citizenship: string;
    institution_id: number;
    institution_name: string;
}

export default function AgencyStudentsTab() {
    const navigate = useNavigate();
    const [agencyStatus, setAgencyStatus] = useState<string | null>(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [selectedInstitution, setSelectedInstitution] = useState<string>("__all__");
    const [search, setSearch] = useState("");
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/agency/status/")
            .then((res) => {
                const s = res.data.status;
                setAgencyStatus(s);
                if (s === "Agreed") fetchStudents({});
            })
            .catch(() => setAgencyStatus("Passive"))
            .finally(() => setStatusLoading(false));

        api.get("/agency/institutions/")
            .then((res) => setInstitutions(res.data.institutions || []))
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchStudents = (overrides: Record<string, string>) => {
        setLoading(true);
        const params: Record<string, string> = {};
        const inst = overrides.institution ?? selectedInstitution;
        const q = overrides.search ?? search;
        if (inst && inst !== "__all__") params.institution = inst;
        if (q.trim()) params.search = q.trim();

        api.get("/agency/students/", { params })
            .then((res) => setStudents(res.data.students || []))
            .catch(() => setStudents([]))
            .finally(() => setLoading(false));
    };

    const handleSearch = () => fetchStudents({});

    if (statusLoading) {
        return (
            <div className="flex flex-col gap-4">
                {[1, 2, 3].map((i) => (
                    <Card key={i} className="h-16 animate-pulse rounded-2xl border border-slate-100" />
                ))}
            </div>
        );
    }

    if (agencyStatus !== "Agreed") {
        return (
            <div className="flex items-center justify-center min-h-[320px]">
                <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white p-10 flex flex-col items-center gap-4 text-center max-w-md">
                    <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center">
                        <AlertCircle className="h-7 w-7 text-amber-500" />
                    </div>
                    <p className="text-base font-bold text-slate-800">Your agreement is not active yet.</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
                <h3 className="text-xl font-serif font-black text-slate-900 tracking-tight">My Students</h3>
            </div>

            <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Applicant Name / Email"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            className="pl-9 h-9 rounded-xl border-slate-200 text-sm"
                        />
                    </div>
                    {institutions.length > 0 && (
                        <Select value={selectedInstitution} onValueChange={setSelectedInstitution}>
                            <SelectTrigger className="h-9 rounded-xl border-slate-200 text-sm w-full sm:w-52">
                                <SelectValue placeholder="All Institutions" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All Institutions</SelectItem>
                                {institutions.map((inst) => (
                                    <SelectItem key={inst.id} value={String(inst.id)}>
                                        {inst.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <Button
                        size="sm"
                        onClick={handleSearch}
                        className="h-9 rounded-xl bg-primary hover:bg-[#002b66] font-bold px-4 shadow-sm"
                    >
                        Search
                    </Button>
                </div>
            </Card>

            {loading ? (
                <div className="flex flex-col gap-3">
                    {[1, 2, 3].map((i) => (
                        <Card key={i} className="h-16 animate-pulse rounded-2xl border border-slate-100" />
                    ))}
                </div>
            ) : students.length === 0 ? (
                <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white p-10 flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-bold text-slate-700">No students found</p>
                    <p className="text-xs text-slate-400">Students you add will appear here.</p>
                </Card>
            ) : (
                <>
                    <div className="flex flex-col gap-3 md:hidden">
                        {students.map((s) => (
                            <Card
                                key={s.id}
                                onClick={() => navigate(`/partner/students/${s.id}`)}
                                className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 space-y-2 cursor-pointer hover:border-primary/20 transition-colors"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-bold text-slate-800">
                                        {s.first_name} {s.last_name}
                                    </span>
                                    <ChevronRight className="h-4 w-4 text-slate-400" />
                                </div>
                                <div className="flex flex-col gap-1 text-xs text-slate-500">
                                    <span className="flex items-center gap-1.5"><Mail className="h-3 w-3" />{s.email || "—"}</span>
                                    <span className="flex items-center gap-1.5"><Phone className="h-3 w-3" />{s.phone || "—"}</span>
                                    <span className="flex items-center gap-1.5"><Globe className="h-3 w-3" />{s.citizenship || "—"}</span>
                                </div>
                                {s.institution_name && (
                                    <Badge variant="outline" className="text-[10px] font-bold rounded-full border-slate-200 text-slate-500">
                                        {s.institution_name}
                                    </Badge>
                                )}
                            </Card>
                        ))}
                    </div>

                    <Card className="hidden md:block border border-slate-100 shadow-sm rounded-2xl overflow-hidden bg-white">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/50 border-b border-slate-100">
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Applicant Name</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Email</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden lg:table-cell whitespace-nowrap">Mobile Phone</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden lg:table-cell whitespace-nowrap">Citizenship</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden xl:table-cell whitespace-nowrap">Institution</th>
                                    <th className="px-6 py-4" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {students.map((s) => (
                                    <tr
                                        key={s.id}
                                        className="group hover:bg-slate-50/30 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/partner/students/${s.id}`)}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 group-hover:bg-primary/5 transition-colors">
                                                    <GraduationCap className="h-4 w-4 text-slate-400 group-hover:text-primary" />
                                                </div>
                                                <span className="text-sm font-medium text-slate-700">
                                                    {s.first_name} {s.last_name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-slate-600">{s.email || "—"}</span>
                                        </td>
                                        <td className="px-6 py-4 hidden lg:table-cell">
                                            <span className="text-sm text-slate-500">{s.phone || "—"}</span>
                                        </td>
                                        <td className="px-6 py-4 hidden lg:table-cell">
                                            <span className="text-sm text-slate-500">{s.citizenship || "—"}</span>
                                        </td>
                                        <td className="px-6 py-4 hidden xl:table-cell">
                                            <span className="text-sm text-slate-500">{s.institution_name || "—"}</span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-primary ml-auto" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </>
            )}
        </div>
    );
}
